import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, dateFR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Wrench, Search, Trash2, ListChecks, Pencil, LogIn, Stethoscope, Hammer, Check, Truck, Download,
} from "lucide-react";
import Plate from "@/components/Plate";
import { toast } from "sonner";
import { downloadXlsx, fmtDate, todayStamp } from "@/lib/csv";

const STATUS_FLOW = [
  { key: "entre",       label: "Entré",      icon: LogIn,       cls: "bg-slate-100 text-slate-700 border-slate-300" },
  { key: "diagnostic",  label: "Diagnostic", icon: Stethoscope, cls: "bg-amber-100 text-amber-800 border-amber-300" },
  { key: "reparation",  label: "Réparation", icon: Hammer,      cls: "bg-orange-100 text-orange-800 border-orange-300" },
  { key: "pret",        label: "Prêt",       icon: Check,       cls: "bg-blue-100 text-blue-800 border-blue-300" },
  { key: "livre",       label: "Livré",      icon: Truck,       cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
];

const STATUS_BY_KEY = Object.fromEntries(STATUS_FLOW.map((s) => [s.key, s]));

export default function VOAtelier() {
  const [items, setItems] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statutFilter, setStatutFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    const [v, i] = await Promise.all([
      api.get("/ventes", { params: { atelier_only: true } }),
      api.get("/interventions"),
    ]);
    setItems(v.data);
    setInterventions(i.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const interMap = useMemo(() => {
    const m = {};
    interventions.forEach((it) => {
      if (!m[it.vente_id]) m[it.vente_id] = [];
      m[it.vente_id].push(it);
    });
    return m;
  }, [interventions]);

  const filtered = useMemo(() => {
    let list = items;
    if (statutFilter !== "all") {
      list = list.filter((v) => (v.atelier_statut || "entre") === statutFilter);
    }
    const t = q.trim().toLowerCase();
    if (t) {
      const cleaned = t.replace(/[-\s]/g, "");
      list = list.filter(
        (v) =>
          (v.plaque || "").toLowerCase().includes(cleaned) ||
          (v.client_nom || "").toLowerCase().includes(t) ||
          (v.marque || "").toLowerCase().includes(t) ||
          (v.modele || "").toLowerCase().includes(t),
      );
    }
    return list;
  }, [items, q, statutFilter]);

  const counts = useMemo(() => {
    const m = { all: items.length };
    STATUS_FLOW.forEach((s) => {
      m[s.key] = items.filter((v) => (v.atelier_statut || "entre") === s.key).length;
    });
    return m;
  }, [items]);

  const setStatus = async (v, key) => {
    if (v.atelier_statut === key) return;
    setItems((prev) => prev.map((x) => (x.id === v.id ? { ...x, atelier_statut: key } : x)));
    try {
      await api.put(`/ventes/${v.id}`, { atelier_statut: key });
      toast.success(`${v.plaque} → ${STATUS_BY_KEY[key].label}`);
    } catch {
      toast.error("Erreur");
      load();
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Mettre le VO atelier ${v.plaque} dans la Corbeille ?\nIl sera conservé 7 jours avec ses interventions, puis effacé définitivement.`)) return;
    try {
      await api.delete(`/ventes/${v.id}`);
      toast.success("VO atelier placé dans la Corbeille (7 jours)");
      load();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const exportExcel = () => {
    if (filtered.length === 0) {
      toast.error("Aucun VO atelier à exporter");
      return;
    }
    const enriched = filtered.map((v) => {
      const inters = interMap[v.id] || [];
      return {
        ...v,
        statut_atelier: STATUS_BY_KEY[v.atelier_statut || "entre"].label,
        nb_interventions: inters.length,
        nb_en_cours: inters.filter((i) => i.statut !== "termine").length,
        nb_terminees: inters.filter((i) => i.statut === "termine").length,
      };
    });
    const columns = [
      { key: "date_vente", label: "Date entrée", format: fmtDate },
      { key: "plaque", label: "Plaque" },
      { key: "marque", label: "Marque" },
      { key: "modele", label: "Modèle" },
      { key: "annee", label: "Année", type: "number" },
      { key: "kilometrage", label: "Kilométrage", type: "number" },
      { key: "couleur", label: "Couleur" },
      { key: "client_nom", label: "Client" },
      { key: "client_telephone", label: "Téléphone" },
      { key: "statut_atelier", label: "Statut atelier" },
      { key: "nb_interventions", label: "Nb interventions", type: "number" },
      { key: "nb_en_cours", label: "En cours", type: "number" },
      { key: "nb_terminees", label: "Terminées", type: "number" },
      { key: "notes", label: "Notes" },
    ];
    downloadXlsx(`vo-atelier-${todayStamp()}`, columns, enriched, "VO Atelier");
    toast.success(`${filtered.length} véhicule(s) exporté(s) (Excel)`);
  };

  return (
    <div className="space-y-6" data-testid="voatelier-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold flex items-center gap-2">
            <Wrench size={12} /> Parc atelier
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1">VO Atelier</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {filtered.length} véhicule(s) confié(s) à l'atelier (hors vente)
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative w-64">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="voatelier-search"
              type="search"
              name="voatelier-search"
              autoComplete="off"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher…"
              className="pl-9 rounded-sm"
            />
          </div>
          <Button
            type="button"
            onClick={exportExcel}
            data-testid="voatelier-export"
            variant="outline"
            className="rounded-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50 whitespace-nowrap"
          >
            <Download size={14} className="mr-1" /> Excel
          </Button>
          <Link to="/atelier">
            <Button data-testid="voatelier-go-add" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
              <Wrench size={14} className="mr-1" /> Ajouter / gérer
            </Button>
          </Link>
        </div>
      </header>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setStatutFilter("all")}
          data-testid="voatelier-filter-all"
          className={`px-3 py-1.5 rounded-sm text-xs font-semibold uppercase tracking-wider transition-colors border ${
            statutFilter === "all" ? "bg-klein text-white border-klein" : "bg-white text-muted-foreground border-border hover:bg-slate-50"
          }`}
        >
          Tous ({counts.all})
        </button>
        {STATUS_FLOW.map((s) => (
          <button
            key={s.key}
            onClick={() => setStatutFilter(s.key)}
            data-testid={`voatelier-filter-${s.key}`}
            className={`px-3 py-1.5 rounded-sm text-xs font-semibold uppercase tracking-wider transition-colors border ${
              statutFilter === s.key ? "bg-klein text-white border-klein" : `${s.cls} hover:opacity-80`
            }`}
          >
            {s.label} ({counts[s.key]})
          </button>
        ))}
      </div>

      {/* Table */}
      <Card className="rounded-sm border-border overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-6 py-3">Actions</th>
              <th className="px-6 py-3">Date entrée</th>
              <th className="px-6 py-3">Plaque</th>
              <th className="px-6 py-3">Véhicule</th>
              <th className="px-6 py-3">Client</th>
              <th className="px-6 py-3">Téléphone</th>
              <th className="px-6 py-3">Statut atelier</th>
              <th className="px-6 py-3 text-center">Interventions</th>
            </tr>
          </thead>
          <tbody data-testid="voatelier-table-body">
            {loading && (
              <tr><td colSpan={8} className="px-6 py-10 text-center text-muted-foreground">Chargement…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="px-6 py-10 text-center text-muted-foreground" data-testid="voatelier-empty">
                Aucun VO atelier. Ajoutez-en un depuis la page Atelier.
              </td></tr>
            )}
            {filtered.map((v) => {
              const list = interMap[v.id] || [];
              const open = list.filter((i) => i.statut !== "termine").length;
              const done = list.filter((i) => i.statut === "termine").length;
              const current = STATUS_BY_KEY[v.atelier_statut || "entre"];
              return (
                <tr key={v.id} data-testid={`voatelier-row-${v.plaque}`} className="border-t hover:bg-slate-50 align-top">
                  <td className="px-6 py-3 whitespace-nowrap">
                    <Link
                      to={`/ventes/${v.id}`}
                      title="Détail"
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-klein/5 hover:bg-klein/10 text-klein border border-klein/20 rounded-sm font-semibold mr-1"
                    >
                      <Pencil size={12} /> Détail
                    </Link>
                    <Link to="/atelier" title="Interventions" className="inline-flex items-center gap-1 px-2 py-1 text-xs hover:bg-slate-100 rounded-sm mr-1">
                      <ListChecks size={12} />
                    </Link>
                    <button
                      onClick={() => remove(v)}
                      title="Supprimer"
                      data-testid={`voatelier-delete-${v.plaque}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-destructive hover:bg-red-50 rounded-sm"
                    >
                      <Trash2 size={12} />
                    </button>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-muted-foreground">{dateFR(v.date_vente)}</td>
                  <td className="px-6 py-3"><Plate value={v.plaque} small /></td>
                  <td className="px-6 py-3">
                    <div className="font-medium">{v.marque} {v.modele}</div>
                    <div className="text-xs text-muted-foreground">
                      {v.annee || "—"} · {v.kilometrage ? `${v.kilometrage.toLocaleString("fr-FR")} km` : "km n/c"}
                    </div>
                  </td>
                  <td className="px-6 py-3">{v.client_nom || "—"}</td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    {v.client_telephone
                      ? <a href={`tel:${v.client_telephone}`} className="text-klein hover:underline font-mono text-xs">{v.client_telephone}</a>
                      : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="px-6 py-3">
                    <Select
                      value={v.atelier_statut || "entre"}
                      onValueChange={(k) => setStatus(v, k)}
                    >
                      <SelectTrigger
                        data-testid={`voatelier-statut-${v.plaque}`}
                        className={`rounded-sm border h-8 text-xs font-semibold uppercase tracking-wider w-[140px] ${current.cls}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_FLOW.map((s) => (
                          <SelectItem key={s.key} value={s.key}>
                            <span className="flex items-center gap-2">
                              <s.icon size={12} /> {s.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-6 py-3 text-center">
                    <div className="flex flex-wrap gap-1 justify-center">
                      {open > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                          {open} en cours
                        </span>
                      )}
                      {done > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {done} terminée{done > 1 ? "s" : ""}
                        </span>
                      )}
                      {list.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
